import { SyntheticEvent, useEffect, useMemo, useState } from "react";
import { Box, NoSsr, Skeleton, Stack } from "@mui/material";
import { useRouter } from "next/router";
import { useTranslation } from "next-i18next";
import { EnumActivityType, useGenericActivityMutation } from "src/api/client/generic-activity.api";
import { ButtonLink } from "src/components/base/button";
import { IphoneCompetitionBanner } from "src/components/pages/news/components/iphone-competition-banner";
import { IUserReactionWithBarConfig } from "src/components/user-reactions/types";
import { UserReactionBar } from "src/components/user-reactions/user-reaction-bar";
import { UserReactionBarLabel } from "src/components/user-reactions/user-reaction-bar-label";
import { hasUserRevealedReactions, setUserRevealedReactions } from "src/components/user-reactions/utils";
import { WfHeading } from "src/components-mui/base/wf-heading/wf-heading";
import { useLinkBuilder } from "src/hooks/use-link-builder";
import { useUserStore } from "src/stores/user-store";
import { VotingTimeLeft } from "../pages/news/components/voting-time-left/voting-time-left";

export interface IUserReactionsProps {
    reactions: ReadonlyArray<IUserReactionWithBarConfig>;
    highestReactionValue: number;
    onReactionChange: (index: number) => (value: number) => void;
    allReactionsCount: number;
    articleCreationDate: string;
    isReactionsDisabled: boolean;
}

const USER_REACTIONS_SHOW_ALL_RESULTS_BUTTON = "user-reactions-show-all-results-button";

export const UserReactions = ({
    reactions,
    highestReactionValue,
    onReactionChange,
    allReactionsCount,
    articleCreationDate,
    isReactionsDisabled,
}: IUserReactionsProps) => {
    const { query } = useRouter();
    const { isLoggedIn } = useUserStore();
    const { openLoginModal } = useLinkBuilder();
    const trackActivity = useGenericActivityMutation();
    const { t } = useTranslation("news");
    const hasUserReactedToAll = useMemo(() => reactions.every(reaction => reaction.hasUserReacted), [reactions]);
    const [showAllBars, setShowAllBars] = useState<boolean>(hasUserReactedToAll || isReactionsDisabled);

    useEffect(() => {
        if (hasUserRevealedReactions(query.slug as string)) {
            setShowAllBars(true);
        }
    }, [query.slug]);

    useEffect(() => {
        setShowAllBars(prevState => prevState || hasUserReactedToAll);
    }, [hasUserReactedToAll]);

    const handleShowAllResultsClick = (e: SyntheticEvent) => {
        if (!isLoggedIn) {
            e.preventDefault();
            return openLoginModal();
        }

        setShowAllBars(true);
        setUserRevealedReactions(query.slug as string);
        trackActivity({ activityTypeId: EnumActivityType.ReactionsRevealed, parameter: "Reactions revealed" });
    };

    return (
        <Box mt={4} sx={{ width: "100%" }}>
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    flexFlow: ["column", "row"],
                    gap: [1, 0],
                    alignItems: ["flex-start", "center"],
                    mb: 2,
                }}
            >
                <WfHeading component="h3" fontSize="xl">
                    {t("sub-headline.reactions")}
                </WfHeading>
                <NoSsr fallback={<Skeleton width="120px" />}>
                    <VotingTimeLeft articleCreationDate={articleCreationDate} />
                </NoSsr>
            </Box>
            {/** To be removed after Iphone campaign ends (14.11.2025) [COM-3755] **/}
            <IphoneCompetitionBanner />
            <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" width="100%" flexWrap="wrap" gap={[3, 4, 3, 4]}>
                {reactions.map((reaction, index) => (
                    <Box key={index} sx={{ maxWidth: { xs: "100%", sm: "315px", md: "100%", lg: "322px" }, width: "100%" }}>
                        <UserReactionBar
                            label={
                                <UserReactionBarLabel
                                    label={reaction.label}
                                    underlyingIsin={reaction.underlyingIsin}
                                    underlyingName={reaction.underlyingName}
                                    underlyingLinkTarget="_self"
                                />
                            }
                            reactionValues={reaction.reactionValues}
                            highestReactionValue={highestReactionValue}
                            selectedValue={reaction.hasUserReacted ? String(reaction.userReactionValue) : undefined}
                            onReactionChange={onReactionChange(index)}
                            hideBar={!reaction.hasUserReacted && !showAllBars}
                            isReactionsDisabled={isReactionsDisabled}
                        />
                    </Box>
                ))}
                {allReactionsCount > 0 ? (
                    <Box sx={{ width: "100%", textAlign: "center" }}>
                        <ButtonLink
                            disabled={showAllBars}
                            sx={showAllBars ? { color: "gray.200", "&:hover": { color: "gray.200" }, cursor: "default" } : undefined}
                            onClick={handleShowAllResultsClick}
                            data-test-id={USER_REACTIONS_SHOW_ALL_RESULTS_BUTTON}
                        >
                            {t("buttons.show-all-results")}
                        </ButtonLink>
                    </Box>
                ) : null}
            </Stack>
        </Box>
    );
};
